import React, { useEffect, useState } from 'react';
import { Modal, Spin, Result, Button, message } from 'antd';
import { EmbedSignDocument } from '@documenso/embed-react';
import axios from '../../utility/axios';
import { useTranslation } from 'react-i18next';
import '../../styles/documenso/DocumensoView.css';

/**
 * Reusable modal for signing Documenso documents.
 * Used by Mailbox, ContactsMailbox, and DocumensoView.
 *
 * @param {boolean} open - Whether the modal is open
 * @param {function} onClose - Callback when modal is closed
 * @param {string} linkId - The documenso link unique identifier (from access_link or access_identifier)
 * @param {Array} documensoFiles - Array of documenso files with envelope_id
 * @param {string} viewerEmail - Email of the viewer/recipient (must match to sign)
 * @param {function} onSuccess - Callback after successful signing (e.g. refresh mailbox)
 */
const DocumensoSignModal = ({ open, onClose, linkId, documensoFiles = [], viewerEmail, onSuccess }) => {
    const { t } = useTranslation();
    const [signModalFile, setSignModalFile] = useState(null);
    const [signModalToken, setSignModalToken] = useState(null);
    const [signModalHost, setSignModalHost] = useState(null);
    const [signModalLoading, setSignModalLoading] = useState(false);
    const [signModalError, setSignModalError] = useState(null);

    useEffect(() => {
        const firstSignable = (documensoFiles || []).find((f) => viewerEmail && f.envelope_id);
        if (!open || !linkId || !firstSignable) return;

        const envelopeId = firstSignable.envelope_id;
        setSignModalFile(firstSignable);
        setSignModalToken(null);
        setSignModalHost(null);
        setSignModalError(null);
        setSignModalLoading(true);

        const fetchToken = async () => {
            try {
                const res = await axios.get(
                    `/api/docuso/signing-token/?link_id=${linkId}&envelope_id=${encodeURIComponent(envelopeId)}`
                );
                setSignModalToken(res.data.token);
                setSignModalHost(res.data.host || null);
                setSignModalError(null);
            } catch (err) {
                const errMsg = err.response?.data?.error || t('messages.view.errorFailedDownload');
                setSignModalError(errMsg);
                setSignModalToken(null);
                setSignModalHost(null);
            } finally {
                setSignModalLoading(false);
            }
        };

        fetchToken();
    }, [open, linkId, documensoFiles, viewerEmail, t]);

    const handleCancel = () => {
        setSignModalToken(null);
        setSignModalHost(null);
        setSignModalError(null);
        setSignModalFile(null);
        onClose();
    };

    const handleDocumentCompleted = () => {
        message.success(t('documenso.signingComplete', 'Document signed successfully'));
        handleCancel();
        onSuccess?.();
    };

    const handleDocumentError = (err) => {
        const errMsg = err?.message || err?.toString?.() || t('documenso.signingError', 'An error occurred during signing');
        message.error(errMsg);
        console.error('[DocumensoSignModal] Embed error:', err);
    };

    return (
        <Modal
            title={signModalFile ? (t('documenso.signDocument', 'Sign document') + ': ' + (signModalFile.file_name || signModalFile.pdf_name)) : (t('documenso.signDocument', 'Sign document'))}
            open={open}
            onCancel={handleCancel}
            footer={null}
            width="95vw"
            style={{ maxWidth: 1400 }}
            bodyStyle={{ height: '70vh', minHeight: 400, padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            destroyOnClose
        >
            {signModalLoading && (
                <div className="documenso-view-sign-modal-loading">
                    <Spin size="large" />
                </div>
            )}
            {signModalError && !signModalLoading && (
                <div className="documenso-view-sign-modal-loading">
                    <Result
                        status="error"
                        title={signModalError}
                        extra={
                            <Button type="primary" onClick={handleCancel}>
                                {t('messages.view.close', 'Close')}
                            </Button>
                        }
                    />
                </div>
            )}
            {signModalToken && !signModalLoading && (
                <div className="documenso-view-sign-embed-container">
                    <EmbedSignDocument
                        token={signModalToken}
                        {...(signModalHost && { host: signModalHost })}
                        onDocumentCompleted={handleDocumentCompleted}
                        onDocumentError={handleDocumentError}
                    />
                </div>
            )}
        </Modal>
    );
};

export default DocumensoSignModal;
